import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { 
  ImageFile, 
  ImageDimensionRange 
} from '../core/model/common/ImageFile.interface';

@Injectable({
  providedIn: 'root'
})


export class ImageValidatorHelperService {

  constructor(
    private toastr: ToastrService
  ) { }

  /**
   * Processes file selection with validation
   * Returns the ImageFile object or null if validation fails
   * Dimension checks only run when maxImageDimension, exactDimensions and/or dimensionRange are passed
   */
  async processFileSelection(
    file: File,
    maxFileSize: number,
    maxImageDimension?: number,
    exactDimensions?: { width: number; height: number },
    dimensionRange?: ImageDimensionRange
  ): Promise<ImageFile | null> {
    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validImageTypes.includes(file.type)) {
      this.toastr.error('Please upload only image files (JPEG, PNG)', 'Invalid File Type');
      return null;
    }

    // Validate file size
    if (file.size > maxFileSize) {
      const maxSizeMB = (maxFileSize / 1024 / 1024).toFixed(2);
      const currentSizeMB = (file.size / 1024 / 1024).toFixed(2);
      this.toastr.error(
        `File size exceeds ${maxSizeMB}MB. Current size: ${currentSizeMB}MB`,
        'File Too Large'
      );
      return null;
    }

    try {
      // Read file as data URL
      const dataUrl = await this.readFileAsDataURL(file);

      // Validate it's actually a valid image
      const isValid = await this.validateImageLoad(dataUrl);

      if (!isValid) {
        this.toastr.error('The file is not a valid image', 'Invalid Image');
        return null;
      }

      // Validate image dimensions (only when the caller asks for it)
      if (maxImageDimension != null || exactDimensions || dimensionRange) {
        const dimensions = await this.getImageDimensionsFromDataURL(dataUrl);
        const current = `Current size: ${dimensions.width}x${dimensions.height}px`;

        if (
          maxImageDimension != null &&
          (dimensions.width > maxImageDimension || dimensions.height > maxImageDimension)
        ) {
          this.toastr.error(
            `Image dimensions must not exceed ${maxImageDimension}x${maxImageDimension}px. ${current}`,
            'Image Too Large'
          );
          return null;
        }

        if (
          exactDimensions &&
          (dimensions.width !== exactDimensions.width || dimensions.height !== exactDimensions.height)
        ) {
          this.toastr.error(
            `Image must be exactly ${exactDimensions.width}x${exactDimensions.height}px. ${current}`,
            'Invalid Image Dimensions'
          );
          return null;
        }

        if (dimensionRange) {
          const { minWidth, maxWidth, minHeight, maxHeight } = dimensionRange;

          const tooSmall =
            (minWidth != null && dimensions.width < minWidth) ||
            (minHeight != null && dimensions.height < minHeight);
          if (tooSmall) {
            this.toastr.error(
              `Image must be at least ${minWidth ?? 0}x${minHeight ?? 0}px. ${current}`,
              'Image Too Small'
            );
            return null;
          }

          const tooLarge =
            (maxWidth != null && dimensions.width > maxWidth) ||
            (maxHeight != null && dimensions.height > maxHeight);
          if (tooLarge) {
            this.toastr.error(
              `Image must not exceed ${maxWidth ?? '∞'}x${maxHeight ?? '∞'}px. ${current}`,
              'Image Too Large'
            );
            return null;
          }
        }
      }

      // Create and return ImageFile object
      const imageFile: ImageFile = {
        link: dataUrl,
        file,
        name: file.name
      };

      this.toastr.success('Image uploaded successfully', 'Success');
      return imageFile;

    } catch (error) {
      console.error('Error processing file:', error);
      this.toastr.error('Failed to read the image file', 'Error');
      return null;
    }
  }

  async validateImage(file: File, maxFileSize: number, maxImageDimension: number): Promise<{ valid: boolean; error?: string }> {

    // Validate file type first
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validImageTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Please upload only image files (JPEG, PNG)'
      };
    }

    // Validate file size
    if (file.size > maxFileSize) {
      const maxSizeMB = (maxFileSize / 1024 / 1024).toFixed(2);
      const currentSizeMB = (file.size / 1024 / 1024).toFixed(2);
      return {
        valid: false,
        error: `File size exceeds ${maxSizeMB}MB. Current size: ${currentSizeMB}MB`
      };
    }

    // Validate image dimensions (max 1000px for both width and height)
    try {
      const dimensions = await this.getImageDimensions(file);

      if (dimensions.width > maxImageDimension || dimensions.height > maxImageDimension) {
        return {
          valid: false,
          error: `Image dimensions must not exceed ${maxImageDimension}x${maxImageDimension}px. Current size: ${dimensions.width}x${dimensions.height}px`
        };
      }

      return { valid: true };
    } catch (error) {
      console.error('Error validating image:', error);
      return {
        valid: false,
        error: 'Error reading image file. Please try again.'
      };
    }
  }

  private getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        const img = new Image();

        img.onload = () => {
          resolve({ width: img.width, height: img.height });
        };

        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };

        img.src = e.target.result;
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(file);
    });
  }

  private readFileAsDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        resolve(e.target.result);
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  }

  private validateImageLoad(dataUrl: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();

      img.onload = () => {
        resolve(true);
      };

      img.onerror = () => {
        resolve(false);
      };

      img.src = dataUrl;
    });
  }

  private getImageDimensionsFromDataURL(dataUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = dataUrl;
    });
  }
}
